import { useState } from 'react'
import {
  MagicTableKebabMenuProps,
  SequenceMenu
} from '@sequencehq/core-components'
import { IntegrationServices } from '@sequencehq/api'
import {
  customCustomerLinkModals,
  LinkCustomerModal,
  UnlinkCustomerModal
} from 'Integrations/index'
import { useQuickBooksCustomerFields } from 'Integrations/integrationsConfig/quickBooks/InspectorItem/useQuickBooksCustomerFields'
import { CustomersView, Integration } from 'modules/Customers/types'
import {
  ArchiveCustomerMenuItem,
  LinkCustomerMenuItem,
  UnlinkCustomerMenuItem,
  ViewCustomerDetailsMenuItem
} from './menuItems'

type ModalState = {
  kind: 'link' | 'unlink'
  service: IntegrationServices
  mountKey: number
}

type Props = {
  model: CustomersView['model']
  connectedIntegrations: Integration[] | null | undefined
} & MagicTableKebabMenuProps

export const CustomerRowKebabMenu = (props: Props) => {
  const { model, connectedIntegrations, ...kebabProps } = props
  const [modalState, setModalState] = useState<ModalState | null>(null)

  const openModal = (kind: 'link' | 'unlink', service: IntegrationServices) => {
    setModalState({ kind, service, mountKey: Date.now() })
  }

  const integrations = (connectedIntegrations ?? []).map(i => i.service)

  return (
    <>
      <SequenceMenu
        data-testid={`customers.${model.id}.menu`}
        buttonSize="small"
        alignment="bottom-right"
        items={[
          ({ setIsOpen }) => (
            <ViewCustomerDetailsMenuItem
              key="view-details"
              customerId={model.id}
              onClose={() => setIsOpen?.(false)}
            />
          ),
          ...integrations.map(service => {
            const alreadyLinked = model.integrationIds.some(
              i => i.service === service
            )
            return ({
              setIsOpen
            }: {
              setIsOpen?: (isOpen: boolean) => void
            }) =>
              alreadyLinked ? (
                <UnlinkCustomerMenuItem
                  key={`unlink-${service}`}
                  customerId={model.id}
                  service={service}
                  onSelect={() => openModal('unlink', service)}
                  onMenuClose={() => setIsOpen?.(false)}
                />
              ) : (
                <LinkCustomerMenuItem
                  key={`link-${service}`}
                  customerId={model.id}
                  service={service}
                  onSelect={() => openModal('link', service)}
                  onMenuClose={() => setIsOpen?.(false)}
                />
              )
          }),
          ({ setIsOpen }) => (
            <ArchiveCustomerMenuItem
              key="archive"
              customerId={model.id}
              disabled={Boolean(model.archivedAt)}
              data-testid={`customers.${model.id}.menuItem.archive`}
              onClose={() => setIsOpen?.(false)}
            />
          )
        ]}
        {...kebabProps}
      />
      {modalState?.kind === 'link' && (
        <LinkCustomerModalRenderer
          key={modalState.mountKey}
          customerId={model.id}
          service={modalState.service}
          onClose={() => setModalState(null)}
        />
      )}
      {modalState?.kind === 'unlink' && (
        <UnlinkCustomerModal
          key={modalState.mountKey}
          customerId={model.id}
          service={modalState.service}
          onUnlink={() => setModalState(null)}
          onClose={() => setModalState(null)}
        />
      )}
    </>
  )
}

const LinkCustomerModalRenderer = (props: {
  customerId: string
  service: IntegrationServices
  onClose: () => void
}) => {
  const {
    serviceSpecificFields,
    serviceSpecificFormFields,
    serviceSpecificQueries
  } = useQuickBooksCustomerFields({
    skip: props.service !== 'QuickBooks_Online'
  })

  const linkModalProps: Record<string, unknown> =
    props.service === 'QuickBooks_Online'
      ? {
          serviceSpecific: {
            fields: serviceSpecificFields,
            formFields: serviceSpecificFormFields,
            isValid: serviceSpecificQueries.isValid
          }
        }
      : {}

  const LinkModalComponent =
    customCustomerLinkModals[props.service] ?? LinkCustomerModal

  return (
    <LinkModalComponent
      customerId={props.customerId}
      service={props.service}
      onLink={props.onClose}
      onClose={props.onClose}
      {...linkModalProps}
    />
  )
}
